import { useState } from "react";
import { Link, useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Building2, Briefcase, Home } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, CURRENCIES } from "@/lib/api";
import { useAuth, homeFor } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const SIDE_IMG = "https://images.pexels.com/photos/31671972/pexels-photo-31671972.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function AuthLayout({ title, subtitle, children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12">
        <Link to="/" className="flex items-center gap-2 mb-12" data-testid="auth-home-link">
          <div className="w-8 h-8 rounded-lg bg-emerald-700 flex items-center justify-center"><Home className="w-4 h-4 text-white" /></div>
          <span className="font-display text-xl font-extrabold">PropNest</span>
        </Link>
        <div className="max-w-md w-full rise">
          <h1 className="text-4xl font-extrabold tracking-tight">{title}</h1>
          {subtitle && <p className="text-slate-500 mt-2 mb-8">{subtitle}</p>}
          {children}
        </div>
      </div>
      <div className="hidden lg:block relative">
        <img src={SIDE_IMG} alt="Georgian terrace" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-slate-900/40" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-200 mb-3">Landlords · Agents · Renters</div>
          <p className="font-display text-3xl font-bold leading-tight max-w-md">Rent, repairs and compliance — sorted before they become problems.</p>
        </div>
      </div>
    </div>
  );
}

const primaryBtn = "w-full bg-emerald-700 hover:bg-emerald-800 text-white h-11 rounded-lg";

export function Login() {
  const { login, session } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  if (session) return <Navigate to={homeFor(session.user.role)} replace />;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const s = await login(email, password);
      nav(homeFor(s.user.role));
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthLayout title="Welcome back" subtitle="Log in as a landlord, letting agent, renter or administrator.">
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <div className="space-y-1.5"><Label>Email</Label><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" /></div>
        <div className="space-y-1.5"><Label>Password</Label><Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" /></div>
        {error && <div className="text-sm text-rose-600" data-testid="login-error">{error}</div>}
        <Button type="submit" disabled={busy} className={primaryBtn} data-testid="login-submit">{busy ? "Signing in…" : "Log in"}</Button>
        <div className="flex justify-between text-sm">
          <Link to="/forgot-password" className="text-emerald-700 hover:underline" data-testid="forgot-link">Forgot password?</Link>
          <Link to="/register" className="text-slate-600 hover:text-slate-900" data-testid="register-link">Create a business account</Link>
        </div>
        <p className="text-xs text-slate-500">Renters: use the login details from your welcome pack email.</p>
      </form>
    </AuthLayout>
  );
}

function TypeOption({ active, onClick, icon: Icon, title, text, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={cn("text-left p-4 rounded-xl border-2 transition-colors", active ? "border-emerald-700 bg-emerald-50" : "border-slate-200 bg-white hover:border-slate-300")}>
      <Icon className={cn("w-5 h-5 mb-2", active ? "text-emerald-700" : "text-slate-400")} />
      <div className="font-semibold text-sm">{title}</div>
      <div className="text-xs text-slate-500 mt-0.5">{text}</div>
    </button>
  );
}

export function Register() {
  const { register, session } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", password: "", business_name: "", business_type: "landlord", currency: "GBP" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  if (session) return <Navigate to={homeFor(session.user.role)} replace />;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await register(f);
      toast.success("Account created — welcome!");
      nav("/app");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthLayout title="Create your account" subtitle="Start free. Upgrade when your portfolio grows.">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div className="grid grid-cols-2 gap-3">
          <TypeOption active={f.business_type === "landlord"} onClick={() => setF({ ...f, business_type: "landlord" })} icon={Building2}
            title="My own properties" text="I'm a landlord or owner" testid="register-type-landlord" />
          <TypeOption active={f.business_type === "agent"} onClick={() => setF({ ...f, business_type: "agent" })} icon={Briefcase}
            title="Letting agent" text="I manage for multiple landlords" testid="register-type-agent" />
        </div>
        <div className="space-y-1.5"><Label>{f.business_type === "agent" ? "Agency name" : "Business / portfolio name"}</Label><Input required value={f.business_name} onChange={set("business_name")} data-testid="register-business-name" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Your name</Label><Input required value={f.name} onChange={set("name")} data-testid="register-name" /></div>
          <div className="space-y-1.5">
            <Label>Currency</Label>
            <Select value={f.currency} onValueChange={(v) => setF({ ...f, currency: v })}>
              <SelectTrigger data-testid="register-currency"><SelectValue /></SelectTrigger>
              <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1.5"><Label>Email</Label><Input type="email" required value={f.email} onChange={set("email")} data-testid="register-email" /></div>
        <div className="space-y-1.5"><Label>Password (min 8 characters)</Label><Input type="password" minLength={8} required value={f.password} onChange={set("password")} data-testid="register-password" /></div>
        {error && <div className="text-sm text-rose-600" data-testid="register-error">{error}</div>}
        <Button type="submit" disabled={busy} className={primaryBtn} data-testid="register-submit">{busy ? "Creating…" : "Create account"}</Button>
        <p className="text-sm text-slate-600">Already have an account? <Link to="/login" className="text-emerald-700 hover:underline" data-testid="login-link">Log in</Link></p>
      </form>
    </AuthLayout>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/forgot-password", { email, origin_url: window.location.origin });
      setSent(true);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <AuthLayout title="Reset password" subtitle="We'll email you a link to choose a new password.">
      {sent ? (
        <div className="panel text-sm" data-testid="forgot-sent">If an account exists for <b>{email}</b>, a reset link is on its way. <Link to="/login" className="text-emerald-700">Back to login</Link></div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5"><Label>Email</Label><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" /></div>
          <Button type="submit" className={primaryBtn} data-testid="forgot-submit">Send reset link</Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/reset-password", { token: params.get("token"), password: pw });
      toast.success("Password reset — please log in");
      nav("/login");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <AuthLayout title="Choose a new password">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5"><Label>New password</Label><Input type="password" minLength={8} required value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password" /></div>
        <Button type="submit" className={primaryBtn} data-testid="reset-submit">Reset password</Button>
      </form>
    </AuthLayout>
  );
}
